import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import { addDealInternalNote, approveDealWin, rejectDealWin, type Deal } from "@/api/client";
import { useCurrentUser } from "@/auth/useCurrentUser";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { CurrencyInput } from "@/components/ui/currency-input";
import { DateRowInput } from "@/components/ui/date-row-input";
import { FormError } from "@/components/ui/form-error";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { dealToasts, useToast } from "@/components/ui/toast";
import { apiErrorMessage } from "@/lib/api-error";
import { dollarsToCents } from "@/lib/currency";
import { CHANNEL_PARTNER } from "@/lib/role-labels";
import { formatAudCents } from "@/lib/spif";

const INPUT_CLASS =
  "flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50";

/** Hold the success toast on screen before finishing (CHP-236, mirrors the
 * registration/extension review 1.5-second hold). */
const REDIRECT_HOLD_MS = 1500;

/** Internal-notes textbox cap — matches DealInternalNotesSection. */
const NOTE_MAX = 2000;

/** Tomorrow (yyyy-mm-dd) — the earliest valid first-payment date (CHP-377:
 * the date must be strictly in the future). Mirrors the backend validator. */
function tomorrowIso(): string {
  return new Date(Date.now() + 86_400_000).toISOString().slice(0, 10);
}

interface Props {
  deal: Deal;
  /** Called after a decision (post toast + 1.5s hold). Drawer → close;
   * standalone route → navigate to the deals dashboard (CHP-236). */
  onDone: () => void;
}

/**
 * The "Verify PO details" body of the closed-won review (CHP-236/CHP-358): the
 * review-against-PO instruction, the mandatory inputs (contract value + first
 * payment date + PO number per CHP-277 — Approve is locked until all are
 * provided), and the Approve / Deny actions with their confirm/deny
 * dialogs.
 *
 * CHP-402: clicking Approve opens a final-check confirm screen ("Approve this
 * Closed-Won request?") that ECHOES the same editable value / date / PO inputs
 * so the reviewer can correct them in place before committing, plus an optional
 * "Internal notes" textbox. The inputs share state with the body, so an edit in
 * either place sticks. On confirm, a non-empty internal note is persisted
 * (POST /deals/{id}/internal-notes, context=closed_won) before the win-approve.
 */
export function DealWinVerifyActions({ deal, onDone }: Props) {
  const { accessToken } = useCurrentUser();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [contractValue, setContractValue] = React.useState("");
  const [paymentDate, setPaymentDate] = React.useState("");
  const [poNumber, setPoNumber] = React.useState("");
  const [internalNote, setInternalNote] = React.useState("");
  const [confirmApprove, setConfirmApprove] = React.useState(false);
  const [confirmReject, setConfirmReject] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  // CHP-809: keep the trigger buttons inert through the post-success hold so a
  // second click can't fire a duplicate decision on the already-decided deal.
  const [finishing, setFinishing] = React.useState(false);

  const holdTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  React.useEffect(
    () => () => {
      if (holdTimer.current) clearTimeout(holdTimer.current);
    },
    [],
  );

  const holdThenDone = React.useCallback(() => {
    setFinishing(true);
    holdTimer.current = setTimeout(() => {
      void queryClient.invalidateQueries({ queryKey: ["deals"] });
      onDone();
    }, REDIRECT_HOLD_MS);
  }, [onDone, queryClient]);

  const approve = useMutation({
    mutationFn: async () => {
      const result = await approveDealWin(accessToken!, deal.id, {
        confirmed_contract_value: dollarsToCents(contractValue),
        first_payment_due_date: paymentDate,
        po_number: poNumber.trim(),
      });
      // CHP-503/504: persist the optional internal note ONLY AFTER the win-approve
      // succeeds. Posting it first meant a 422 on approve (the corrected-value
      // retry path) re-POSTed the same note on the next confirm → a duplicate
      // append-only note row. Approve is the gating action; if it fails we never
      // touch notes, and the note is cleared on success below so a happy-path
      // confirm posts it exactly once. The existing notes endpoint (notes-infra
      // lane) is reused — no new backend.
      const note = internalNote.trim();
      if (note) {
        await addDealInternalNote(accessToken!, deal.id, { body: note, context: "closed_won" });
      }
      return result;
    },
    onSuccess: () => {
      setConfirmApprove(false);
      setInternalNote("");
      void queryClient.invalidateQueries({ queryKey: ["deals", "internal-notes", deal.id] });
      toast(dealToasts.winReviewApproved());
      holdThenDone();
    },
    onError: (err) =>
      setError(apiErrorMessage(err, "Approval failed. Check the values and try again.")),
  });

  const reject = useMutation({
    mutationFn: (reason: string) => rejectDealWin(accessToken!, deal.id, reason),
    onSuccess: () => {
      setConfirmReject(false);
      toast(dealToasts.winReviewDenied());
      holdThenDone();
    },
    onError: (err) => setError(apiErrorMessage(err, "Denial failed. Refresh and try again.")),
  });

  const anyPending = approve.isPending || reject.isPending;

  if (deal.deal_status !== "win_pending_review") return null;

  const inert = anyPending || finishing;
  // Gate on the rounded CENTS, not the raw float: a sub-cent value like "0.001"
  // is `parseFloat > 0` but rounds to 0 cents, which the backend `gt=0` rejects
  // with a 422. Requiring positive cents keeps Approve disabled until the value
  // is actually payable (CHP-503/504 minor).
  const valueValid = contractValue !== "" && dollarsToCents(contractValue) > 0;
  const dateValid = paymentDate !== "";
  const poValid = poNumber.trim() !== "";
  const approveDisabled = !valueValid || !dateValid || !poValid || inert;

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        Review contract value and payment date against the purchase order. Approve to transition
        deal to &apos;Closed-Won&apos;, or deny if details are mismatched.
      </p>

      <div className="space-y-1">
        <Label htmlFor="win-contract-value">Contract value (AUD) *</Label>
        {/* The partner's unofficial estimate from win submission — a reference
            figure beside the mandatory confirmed-value input (gap-build W2). */}
        {deal.partner_estimated_value != null ? (
          <p className="text-sm text-muted-foreground" data-testid="partner-estimate-reference">
            Partner estimate (unofficial): {formatAudCents(deal.partner_estimated_value)}
          </p>
        ) : null}
        <CurrencyInput
          id="win-contract-value"
          value={contractValue}
          onValueChange={setContractValue}
          className={INPUT_CLASS}
          placeholder="e.g. 500,000"
        />
      </div>

      <div className="space-y-1">
        <Label htmlFor="win-payment-date">First payment date *</Label>
        {/* CHP-396: whole-row click opens the picker. */}
        <DateRowInput
          id="win-payment-date"
          value={paymentDate}
          min={tomorrowIso()}
          onChange={(e) => setPaymentDate(e.target.value)}
          wrapperClassName="bg-background"
          className="py-2"
        />
      </div>

      <div className="space-y-1">
        <Label htmlFor="win-po-number">PO number *</Label>
        <input
          id="win-po-number"
          type="text"
          maxLength={100}
          value={poNumber}
          onChange={(e) => setPoNumber(e.target.value)}
          className={INPUT_CLASS}
          placeholder="Customer purchase order number"
        />
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          disabled={approveDisabled}
          onClick={() => {
            setError(null);
            setConfirmApprove(true);
          }}
        >
          Approve
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={inert}
          onClick={() => {
            setError(null);
            setConfirmReject(true);
          }}
        >
          Deny
        </Button>
      </div>

      {/* CHP-402: the Approve final-check. The value/date/PO inputs are echoed
          here (same state as the body) so the reviewer can correct them in
          place, and an optional internal-notes box is included as on the other
          decision pages. */}
      <ConfirmDialog
        open={confirmApprove}
        onOpenChange={setConfirmApprove}
        title="Approve this Closed-Won request?"
        confirmLabel="Confirm"
        pending={approve.isPending}
        // CHP-809: the dialog echoes the editable mandatory inputs, so re-gate
        // Confirm on their validity — clearing a field inside the dialog must
        // re-disable Confirm so no NaN/empty value/date/PO is ever submitted.
        confirmDisabled={!valueValid || !dateValid || !poValid}
        onConfirm={() => {
          setError(null);
          approve.mutate();
        }}
      >
        <div className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="win-approve-value">Contract value (AUD) *</Label>
            <CurrencyInput
              id="win-approve-value"
              value={contractValue}
              onValueChange={setContractValue}
              className={INPUT_CLASS}
              placeholder="e.g. 500,000"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="win-approve-date">First payment date *</Label>
            <DateRowInput
              id="win-approve-date"
              value={paymentDate}
              min={tomorrowIso()}
              onChange={(e) => setPaymentDate(e.target.value)}
              wrapperClassName="bg-background"
              className="py-2"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="win-approve-po">PO number *</Label>
            <input
              id="win-approve-po"
              type="text"
              maxLength={100}
              value={poNumber}
              onChange={(e) => setPoNumber(e.target.value)}
              className={INPUT_CLASS}
              placeholder="Customer purchase order number"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="win-approve-note">Internal notes (optional)</Label>
            <p className="text-xs text-muted-foreground">
              Visible to ResetData staff only. Never shared with the channel partner.
            </p>
            <Textarea
              id="win-approve-note"
              value={internalNote}
              onChange={(e) => setInternalNote(e.target.value)}
              maxLength={NOTE_MAX}
              rows={3}
              placeholder="Visible to ResetData staff only. Never shared with the channel partner."
            />
          </div>
          {/* CHP-503/504: the approve error MUST render inside the dialog. The
              ConfirmDialog is a Radix portal with a z-50 overlay, so a root-level
              FormError would be painted beneath it — invisible to the reviewer.
              Showing it here keeps the echoed value/date/PO inputs on screen so
              they can correct them in place. */}
          {error ? <FormError>{error}</FormError> : null}
        </div>
      </ConfirmDialog>

      <ConfirmDialog
        open={confirmReject}
        onOpenChange={setConfirmReject}
        title="Deny this Closed-Won request?"
        variant="destructive"
        confirmLabel="Confirm denial"
        reason={{
          label: `Reason for Decision (Sent to ${CHANNEL_PARTNER})`,
          required: true,
          maxLength: 500,
        }}
        pending={reject.isPending}
        onConfirm={(reason) => {
          setError(null);
          reject.mutate(reason ?? "");
        }}
      >
        {/* Same occlusion rule as the approve dialog: a reject failure (e.g. a
            409 illegal transition) renders inside the portal, not behind it. */}
        {error ? <FormError>{error}</FormError> : null}
      </ConfirmDialog>
    </div>
  );
}
